import { Alert, Button, Drawer, Spin } from "@arco-design/web-react";
import { FileDown, MoreHorizontal, MoveRight, QrCode, Table2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../shared/ui/dropdown-menu";
import { ButtonGroup } from "../../../shared/ui/button-group";
import * as itineraryRepo from "../repo/itineraryRepo";
import {
  derivePublicItinerary,
  outputOperationErrorMessage,
} from "../service/itineraryOutputService";
import { downloadItineraryPdf } from "../service/itineraryPdfService";
import type {
  ItineraryCustomerOutput,
  ItineraryEditor,
  ItineraryInternalInteractions,
  ItineraryInternalMarkResult,
  ItineraryMarkType,
  ItinerarySuggestion,
  ShareLinkResult,
} from "../types";
import { ItineraryClientDetail } from "./ItineraryClientDetail";
import { ItineraryDialogCloseButton } from "./ItineraryDialogCloseButton";
import { ItineraryInternalActions } from "./ItineraryInternalActions";
import { ItineraryShareQrModal } from "./ItineraryShareQrModal";

type Props = {
  visible: boolean;
  initialView?: "preview" | "detail";
  initialDayId?: string;
  initialDayNumber?: number;
  editor?: ItineraryEditor;
  onClose: () => void;
  afterClose?: () => void;
  ensureSaved?: () => Promise<boolean>;
  loadEditor?: (itineraryId: string) => Promise<ItineraryEditor>;
  createShareLink?: (itineraryId: string) => Promise<ShareLinkResult>;
  canExport?: boolean;
  canShare?: boolean;
  canViewTable?: boolean;
  loading?: boolean;
  errorMessage?: string;
  warningMessage?: string;
  onRetry?: () => void;
  copyActions?: ReactNode;
  moveActions?: ReactNode;
  onStartMove?: () => void;
  onCancelMove?: () => void;
  movePending?: boolean;
  lifecycleActions?: ReactNode;
  notice?: { type: "success" | "error"; text: string; action?: ReactNode };
  loadCustomerOutput?: (itineraryId: string, signal?: AbortSignal) => Promise<ItineraryCustomerOutput>;
  enableInternalInteractions?: boolean;
  loadInternalInteractions?: (itineraryId: string) => Promise<ItineraryInternalInteractions>;
  toggleInternalMark?: (
    itineraryId: string,
    markType: ItineraryMarkType,
  ) => Promise<ItineraryInternalMarkResult>;
  createSuggestion?: (
    itineraryId: string,
    content: string,
    parentId?: string | null,
  ) => Promise<ItinerarySuggestion>;
};

/** 后台客户输出抽屉。 */
export function ItineraryOutputDrawer({ visible, initialDayId, initialDayNumber, editor, onClose, afterClose, ensureSaved, loadEditor, createShareLink = itineraryRepo.createShareLink, loadCustomerOutput = itineraryRepo.getItineraryCustomerOutput, canExport = true, canShare = true, canViewTable = false, loading = false, errorMessage, warningMessage, onRetry, copyActions, moveActions, onStartMove, onCancelMove, movePending = false, lifecycleActions, notice, enableInternalInteractions = false, loadInternalInteractions, toggleInternalMark, createSuggestion }: Props) {
  const closeOutput = () => {
    if (movePending) return;
    onCancelMove?.();
    onClose();
  };
  const editorIdRef = useRef(editor?.id);
  editorIdRef.current = editor?.id;
  const shareRequestRef = useRef(0);
  const [sharingItineraryId, setSharingItineraryId] = useState<string>();
  const [qrShare, setQrShare] = useState<{ itineraryId: string; url: string }>();
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [wordDownloading, setWordDownloading] = useState(false);
  const wordOperationRef = useRef(false);
  const wordRequestRef = useRef(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loadedEditor, setLoadedEditor] = useState<ItineraryEditor>();
  const [editorLoadFailed, setEditorLoadFailed] = useState(false);
  const [editorLoadAttempt, setEditorLoadAttempt] = useState(0);
  const [customerOutput, setCustomerOutput] = useState<{
    requestKey: string;
    status: "loading" | "loaded" | "error";
    value?: ItineraryCustomerOutput;
  }>();
  const [customerOutputAttempt, setCustomerOutputAttempt] = useState(0);
  const [message, setMessage] = useState<{
    itineraryId: string;
    type: "alert";
    text: string;
  }>();
  const currentMessage = message?.itineraryId === editor?.id ? message : undefined;
  const currentQrShare = qrShare && qrShare.itineraryId === editor?.id ? qrShare : undefined;
  const contentRef = useRef<HTMLElement>(null);
  const attachContent = useCallback((content: HTMLElement | null) => {
    contentRef.current = content;
    // Portal 挂载后只在打开时聚焦阅读区，保留 Esc 且不打断异步加载后的输入。
    if (visible) content?.focus({ preventScroll: true });
  }, [visible]);
  const pdfOperationRef = useRef(false);
  const outputEditor = loadEditor
    ? loadedEditor?.id === editor?.id ? loadedEditor : undefined
    : editor;
  const customerOutputRequestKey = visible && outputEditor
    ? `${outputEditor.id}:${customerOutputAttempt}`
    : undefined;
  const currentCustomerOutput = customerOutputRequestKey
    && customerOutput?.requestKey === customerOutputRequestKey
    && customerOutput.status === "loaded"
    ? customerOutput.value
    : undefined;
  const customerOutputLoading = Boolean(customerOutputRequestKey && (
    customerOutput?.requestKey !== customerOutputRequestKey
    || customerOutput.status === "loading"
  ));
  const customerOutputLoadFailed = Boolean(customerOutputRequestKey
    && customerOutput?.requestKey === customerOutputRequestKey
    && customerOutput.status === "error");
  const output = outputEditor && currentCustomerOutput
    ? derivePublicItinerary(
        outputEditor,
        currentCustomerOutput,
      )
    : undefined;
  const outputUnavailable = !output;
  const outputInitialDayNumber = initialDayId
    ? outputEditor?.days.find((day) => day.id === initialDayId)?.dayNumber
    : initialDayNumber;
  const loadingPersistedEditor = Boolean(
    visible && editor && loadEditor && !outputEditor && !editorLoadFailed,
  );
  const persistedEditorError = editorLoadFailed
    ? "MySQL 已保存版本加载失败，请重试。"
    : undefined;
  useEffect(() => {
    setLoadedEditor(undefined);
    setEditorLoadFailed(false);
    if (!visible || !editor || !loadEditor) return;
    const itineraryId = editor.id;
    let active = true;
    void loadEditor(itineraryId).then((next) => {
      if (active && editorIdRef.current === itineraryId) setLoadedEditor(next);
    }).catch(() => {
      if (active && editorIdRef.current === itineraryId) setEditorLoadFailed(true);
    });
    return () => {
      active = false;
    };
  }, [editor?.id, editorLoadAttempt, loadEditor, visible]);
  useEffect(() => {
    setCustomerOutput(undefined);
    if (!visible || !outputEditor) return;
    const itineraryId = outputEditor.id;
    const requestKey = `${itineraryId}:${customerOutputAttempt}`;
    const controller = new AbortController();
    setCustomerOutput({ requestKey, status: "loading" });
    void loadCustomerOutput(itineraryId, controller.signal).then((next) => {
      if (!controller.signal.aborted && editorIdRef.current === itineraryId) {
        setCustomerOutput({ requestKey, status: "loaded", value: next });
      }
    }).catch(() => {
      if (!controller.signal.aborted && editorIdRef.current === itineraryId) {
        setCustomerOutput({ requestKey, status: "error" });
      }
    });
    return () => controller.abort();
  }, [customerOutputAttempt, loadCustomerOutput, outputEditor?.id, visible]);
  useEffect(() => {
    shareRequestRef.current += 1;
    wordRequestRef.current += 1;
    if (visible) {
      setMobileMenuOpen(false);
      setSharingItineraryId(undefined);
      setQrShare(undefined);
      setMessage(undefined);
      const drawerContent = contentRef.current?.closest<HTMLElement>(".arco-drawer-content");
      const drawerScroll = contentRef.current?.closest<HTMLElement>(".arco-drawer-scroll");
      if (drawerContent) drawerContent.scrollTop = 0;
      if (drawerScroll) drawerScroll.scrollTop = 0;
    }
    return () => { shareRequestRef.current += 1; wordRequestRef.current += 1; };
  }, [editor?.id, visible]);
  useEffect(() => {
    if (!visible) return;
    const releaseToolbarFocus = () => {
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement && activeElement.closest(".itinerary-output-toolbar")) {
        activeElement.blur();
      }
    };
    document.addEventListener("wheel", releaseToolbarFocus, { capture: true, passive: true });
    document.addEventListener("touchstart", releaseToolbarFocus, { capture: true, passive: true });
    return () => {
      document.removeEventListener("wheel", releaseToolbarFocus, true);
      document.removeEventListener("touchstart", releaseToolbarFocus, true);
    };
  }, [visible]);
  const ensureCurrentSaved = async (itineraryId: string) => {
    if (!ensureSaved || await ensureSaved()) return editorIdRef.current === itineraryId;
    if (editorIdRef.current === itineraryId) {
      setMessage({
        itineraryId,
        type: "alert",
        text: "保存未完成：当前有尚未写入服务器的本地草稿，请先点击顶部保存，再执行客户交付操作。",
      });
    }
    return false;
  };

  const share = async () => {
    if (!editor) return;
    const itineraryId = editor.id;
    const requestId = ++shareRequestRef.current;
    const isCurrent = () => shareRequestRef.current === requestId && editorIdRef.current === itineraryId;
    setSharingItineraryId(itineraryId);
    setMessage(undefined);
    try {
      if (!await ensureCurrentSaved(itineraryId)) return;
      if (!isCurrent()) return;
      const result = await createShareLink(itineraryId);
      if (!isCurrent()) return;
      const absoluteUrl = new URL(result.url, window.location.origin).toString();
      setQrShare({ itineraryId, url: absoluteUrl });
    } catch (error) {
      if (isCurrent()) {
        setMessage({
          itineraryId,
          type: "alert",
          text: outputOperationErrorMessage("share", error),
        });
      }
    } finally {
      if (isCurrent()) setSharingItineraryId(undefined);
    }
  };

  const downloadPdf = async (includeDayImages = true) => {
    if (pdfOperationRef.current || !output) return;
    const itineraryId = editor?.id;
    pdfOperationRef.current = true;
    setPdfDownloading(true);
    setMessage(undefined);
    try {
      if (!itineraryId || !await ensureCurrentSaved(itineraryId)) return;
      await downloadItineraryPdf(itineraryId, output.title || "客户行程", includeDayImages);
    } catch (error) {
      if (itineraryId && editorIdRef.current === itineraryId) {
        setMessage({
          itineraryId,
          type: "alert",
          text: outputOperationErrorMessage("pdf", error),
        });
      }
    } finally {
      pdfOperationRef.current = false;
      setPdfDownloading(false);
    }
  };

  const downloadWord = async () => {
    if (wordOperationRef.current || !canExport || !output || !editor) return;
    const itineraryId = editor.id;
    const requestId = ++wordRequestRef.current;
    const isCurrent = () => wordRequestRef.current === requestId && editorIdRef.current === itineraryId;
    wordOperationRef.current = true;
    setWordDownloading(true);
    setMessage(undefined);
    try {
      const snapshot = await itineraryRepo.getReadonlyPreview(itineraryId, true);
      if (!isCurrent()) return;
      const [{ renderItineraryWordContent }, { createItineraryWordBlob, downloadItineraryWordBlob }] = await Promise.all([
        import("./ItineraryWordDocument"), import("../service/itineraryWordService"),
      ]);
      if (!isCurrent()) return;
      const title = snapshot.itinerary.title || "客户行程";
      const blob = await createItineraryWordBlob(renderItineraryWordContent(snapshot.itinerary), title);
      if (isCurrent()) downloadItineraryWordBlob(blob, title);
    } catch {
      if (isCurrent()) setMessage({ itineraryId, type: "alert", text: "Word 下载失败，请稍后重试。" });
    } finally {
      wordOperationRef.current = false;
      setWordDownloading(false);
    }
  };

  const runMobileOperation = (operation: string) => {
    setMobileMenuOpen(false);
    if (operation === "pdf") void downloadPdf();
    if (operation === "pdf-without-images") void downloadPdf(false);
    if (operation === "word") void downloadWord();
    if (operation === "qr") void share();
  };

  const operationPending = pdfDownloading || wordDownloading || Boolean(sharingItineraryId);

  const mobileOperationMenu = <DropdownMenuContent
    className="itinerary-output-mobile-menu"
    side="top"
    sideOffset={4}
    align="start"
  >
    <DropdownMenuGroup>
    {canViewTable ? <DropdownMenuItem disabled={!outputEditor} onSelect={() => {
      setMobileMenuOpen(false);
      if (editor) window.open(`/itineraries/${encodeURIComponent(editor.id)}/table`, "_blank", "noopener,noreferrer");
    }}><Table2 size={15} aria-hidden="true" /><span>行程表格</span></DropdownMenuItem> : null}
    {canExport ? <>
      <DropdownMenuItem disabled={outputUnavailable || pdfDownloading} onSelect={() => runMobileOperation("pdf")}>
        <FileDown size={15} aria-hidden="true" />
        <span>PDF</span>
      </DropdownMenuItem>
      <DropdownMenuItem disabled={outputUnavailable || pdfDownloading} onSelect={() => runMobileOperation("pdf-without-images")}>
        <FileDown size={15} aria-hidden="true" />
        <span>PDF（不含图）</span>
      </DropdownMenuItem>
    </> : null}
    {canShare ? <DropdownMenuItem disabled={outputUnavailable || Boolean(sharingItineraryId)} onSelect={() => runMobileOperation("qr")}>
      <QrCode size={15} aria-hidden="true" />
      <span>分享</span>
    </DropdownMenuItem> : null}
    {canExport ? <DropdownMenuItem disabled={outputUnavailable || wordDownloading} onSelect={() => runMobileOperation("word")}>
      <FileDown size={15} aria-hidden="true" />
      <span>Word</span>
    </DropdownMenuItem> : null}
    </DropdownMenuGroup>
  </DropdownMenuContent>;

  const actionBar = editor ? <div className="itinerary-output-toolbar" role="toolbar" aria-label="行程操作">
    {moveActions ? <div className="itinerary-output-move-actions">{moveActions}</div> : <>
    {copyActions ? <div className="itinerary-output-copy-actions">{copyActions}</div> : null}
    <div className="itinerary-output-actions-row">
      <div className="itinerary-output-export-actions">{canExport ? <>
        <Button disabled={outputUnavailable} aria-label="下载 PDF" icon={<FileDown size={15} />} loading={pdfDownloading} onClick={() => void downloadPdf()}>PDF</Button>
        <Button disabled={outputUnavailable} aria-label="下载 PDF（不含图）" icon={<FileDown size={15} />} loading={pdfDownloading} onClick={() => void downloadPdf(false)}>PDF（不含图）</Button>
      </> : null}
      {canShare ? <Button
          className="itinerary-share-qr-trigger"
          icon={<QrCode size={15} />}
          loading={sharingItineraryId === editor.id}
          disabled={outputUnavailable || Boolean(sharingItineraryId)}
          onClick={() => void share()}
        >分享</Button> : null}
      {canViewTable ? <Button icon={<Table2 size={15} />} disabled={!outputEditor} onClick={() => window.open(`/itineraries/${encodeURIComponent(editor.id)}/table`, "_blank", "noopener,noreferrer")}>行程表格</Button> : null}
      {canExport ? <Button disabled={outputUnavailable} aria-label="下载 Word" icon={<FileDown size={15} />} loading={wordDownloading} onClick={() => void downloadWord()}>Word</Button> : null}
      {onStartMove ? <Button icon={<MoveRight size={15} />} disabled={!outputEditor || operationPending} onClick={onStartMove}>移动行程</Button> : null}
      </div>
      <ButtonGroup className="itinerary-output-button-group">
        {<DropdownMenu open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              className="itinerary-output-mobile-more"
              aria-label="更多操作"
              title="更多操作"
              type="secondary"
              icon={<MoreHorizontal size={16} aria-hidden="true" />}
              loading={operationPending}
              disabled={!outputEditor || operationPending}
            >更多操作</Button>
          </DropdownMenuTrigger>
          {mobileOperationMenu}
        </DropdownMenu>}
        {lifecycleActions ? <div className="itinerary-output-lifecycle-actions">{lifecycleActions}</div> : null}
      </ButtonGroup>
    </div>
    <ItineraryDialogCloseButton label="关闭行程详情" onClick={closeOutput} />
    </>}
    {currentMessage ? <p className={`itinerary-output-message is-${currentMessage.type}`} role={currentMessage.type}>
      <span>{currentMessage.text}</span>
    </p> : null}
  </div> : <div className="itinerary-output-toolbar is-state">
    <strong>行程详情</strong>
    <ItineraryDialogCloseButton label="关闭行程预览" onClick={closeOutput} />
  </div>;

  return (
    <>
      <Drawer
      className="itinerary-output-drawer"
      wrapClassName="itinerary-output-drawer-wrapper"
      aria-label="行程详情"
      title={null}
      closable={false}
      visible={visible}
      autoFocus={false}
      width={980}
      zIndex={1300}
      getPopupContainer={() => document.body}
      footer={actionBar}
      onCancel={closeOutput}
      afterClose={afterClose}
    >
      <section ref={attachContent} tabIndex={-1} className="itinerary-output-content-inner" role="dialog" aria-modal="true" aria-label="行程详情" aria-hidden={!visible}>
      {notice ? <Alert type={notice.type} showIcon content={notice.text} action={notice.action} /> : null}
      {warningMessage ? <Alert type="warning" showIcon content={warningMessage} /> : null}
      {loading || loadingPersistedEditor || errorMessage || persistedEditorError ? <div className="itinerary-output-state">
        {loading || loadingPersistedEditor ? <Spin block tip="正在加载行程" /> : <Alert
          type="error"
          showIcon
          content={errorMessage ?? persistedEditorError}
          action={errorMessage
            ? onRetry ? <Button size="small" onClick={onRetry}>重试</Button> : undefined
            : <Button size="small" onClick={() => setEditorLoadAttempt((attempt) => attempt + 1)}>重试</Button>}
        />}
      </div> : customerOutputLoading ? <div className="itinerary-output-state">
        <Spin block tip="正在加载客户补充信息" />
      </div> : customerOutputLoadFailed ? <div className="itinerary-output-state">
        <Alert
          type="error"
          showIcon
          content="客户补充信息加载失败，当前无法确认完整客户输出。请重试后再预览或交付。"
          action={<Button size="small" onClick={() => setCustomerOutputAttempt((attempt) => attempt + 1)}>
            重试加载客户信息
          </Button>}
        />
      </div> : output ? <div id="itinerary-output-drawer-panel" className="itinerary-phone-frame" role="region" aria-label="行程详情内容">
        <ItineraryClientDetail
          itinerary={output}
          initialDayNumber={outputInitialDayNumber}
          internalActions={enableInternalInteractions && editor ? (
            <ItineraryInternalActions
              itineraryId={editor.id}
              enabled={visible}
              loadInternalInteractions={loadInternalInteractions}
              toggleInternalMark={toggleInternalMark}
              createSuggestion={createSuggestion}
            />
          ) : undefined}
        />
      </div> : null}
      </section>
      </Drawer>
      <ItineraryShareQrModal
        visible={Boolean(visible && currentQrShare)}
        shareUrl={currentQrShare?.url ?? ""}
        onClose={() => setQrShare(undefined)}
      />
    </>
  );
}
